import { Data, Either, Schema } from "effect";
import type { SpanLocator } from "./block-locator.ts";

// A Locator addresses a region of a section's text (with existing CriticMarkup
// normalized away). It is resolved once at write time and rejected on a zero or
// ambiguous match (ADR-0001); after a write the durable anchor is the id, not the
// locator.
//
// Block-locator refactor plan (U2): every LIVE domain caller — the `annotate` MCP tool,
// `search-document`'s matches, `write-annotation`'s args — has moved to `SpanLocator`
// (`block-locator.ts`): a `BlockLocator` plus literal text unique within that one block. This
// four-variant union (`span`/`region`/`boundary`/`anchored`) is KEPT, unchanged, for two callers
// that cannot cleanly migrate to it (verified, not assumed — see each site):
//   1. `review.ts`'s `EditOperation` (`Insert`/`Delete`/`Substitute`), embedded in the
//      `suggestion.created`/`suggestion.amended`/`edit.proposed` audit events `audit.ts` explicitly
//      marks "COMPAT RULE: do NOT delete or rename — old JSONL/Postgres logs must still decode".
//      Their locators also predate block scoping (`sectionLocator` builds a whole-block `region`;
//      `source-edit.ts`'s free-form diff hunks can span or straddle block boundaries), so even were
//      the compat rule not binding, "unique text within one block" is not a faithful reshape of
//      "an arbitrary diff hunk's exact span". (The client-side callers of this path — `source-edit.ts`,
//      `gate-actions.ts`'s `proposeEdit`, `routes.tsx`'s Remove button — are themselves already dead:
//      `POST /api/propose-edit` was deleted server-side in the collab pivot, D9. Cleaning that up is
//      out of U2's scope; see U9 / a follow-up.)
//   2. `check-suite.ts`'s `anchorSpan`: an internal offset-computation detail, never wire-exposed
//      (only the resolved `{start,end}` reaches `writeStructuredAnnotation`'s `StructuredAnchor`).
//      A cross-unit check's `ContentPart` has an EMPTY `headingPath` (it spans the whole outline, no
//      single block), so its finding cannot be named by a `BlockLocator` at all — forcing `SpanLocator`
//      here would drop cross-unit anchoring, not simplify it.

// An exact text span. Resolves to the single occurrence in the section; zero
// matches → SpanNotFound, more than one → SpanAmbiguous (enforced in U5).
export const Span = Schema.TaggedStruct("span", { text: Schema.String });
export type Span = typeof Span.Type;

// An inclusive range from one anchor text to another.
export const Region = Schema.TaggedStruct("region", {
  from: Schema.String,
  to: Schema.String,
});
export type Region = typeof Region.Type;

// A zero-width insertion point relative to an anchor.
export const Boundary = Schema.TaggedStruct("boundary", {
  anchor: Schema.String,
  side: Schema.Literal("before", "after"),
});
export type Boundary = typeof Boundary.Type;

// An exact text span disambiguated by its immediate surroundings. Unlike a Region (which spans
// anchor-to-anchor), this resolves to *only* `text`: `before`/`after` are the contiguous neighbours
// that make `before + text + after` unique, never part of the located region. Zero matches →
// SpanNotFound, more than one → SpanAmbiguous. Lets a caller pin a repeated word to the exact
// occurrence the user selected without widening the located span.
export const Anchored = Schema.TaggedStruct("anchored", {
  before: Schema.String,
  text: Schema.String,
  after: Schema.String,
});
export type Anchored = typeof Anchored.Type;

export const Locator = Schema.Union(Span, Region, Boundary, Anchored);
export type Locator = typeof Locator.Type;

export const span = (text: string): Locator => Span.make({ text });
export const region = (from: string, to: string): Locator => Region.make({ from, to });
export const boundary = (anchor: string, side: "before" | "after"): Locator =>
  Boundary.make({ anchor, side });
export const anchored = (before: string, text: string, after: string): Locator =>
  Anchored.make({ before, text, after });

// ─── Plain-text locator resolution (collab era, D12) ─────────────────────────
//
// In the collab world the document body is plain markdown — review state has left the
// bytes (annotations are version-keyed diagnostics, suggestions are draft buffers). A
// locator therefore resolves directly against plain head text, with no CriticMarkup
// normalization step. `resolvePlainLocator` is the pure, markup-free sibling of markup's
// `resolveLocator`: same `SpanNotFound`/`SpanAmbiguous` uniqueness contract (ADR-0001,
// D12), returning head-relative half-open offsets `{ from, to }`. These errors live in
// core (not markup) because the collab-era anchor resolver no longer belongs to the
// retiring markup engine.

// The locator resolved to no occurrence in the text.
//
// `editIndex` is vestigial (it named the offending edit in the old batch `draft-edit`, retired by
// the block-locator refactor plan's KTD3 — every edit tool is single-target now, so nothing sets it
// any more). `needle` (the actual searched-for text) is what every renderer names in its message —
// `locator` itself is never read anywhere, only constructed, so U2 reconciles it away from the
// retired union rather than forcing a fake shape onto it: optional, and typed `SpanLocator` (the
// live shape) when a producer has one to give (`resolveSpanLocator`); the two KEPT old-union
// resolvers (`resolvePlainLocator` below, `locator-resolve.ts`'s raw-text resolver) simply omit it.
export class SpanNotFound extends Data.TaggedError("SpanNotFound")<{
  readonly locator?: SpanLocator;
  readonly editIndex?: number;
  readonly needle?: string;
}> {}

// The locator resolved to more than one occurrence — the caller must disambiguate.
//
// `editIndex`/`locator` are as `SpanNotFound` above. `needle`/`candidates` are set by
// `locator-resolve.ts`'s raw-text resolver (the check-suite path): the ambiguous search text and up
// to `CANDIDATE_CAP` ready-made document-unique locators (each carrying before/after context) the
// agent can pin the intended occurrence with — these stay the OLD union shape (`Locator`), since
// they are minted only by that kept internal resolver, never surfaced to the agent as a paste target
// post-migration. `count` is always present (the raw resolver sets it).
export class SpanAmbiguous extends Data.TaggedError("SpanAmbiguous")<{
  readonly locator?: SpanLocator;
  readonly count: number;
  readonly editIndex?: number;
  readonly needle?: string;
  readonly candidates?: ReadonlyArray<Locator>;
}> {}

// The maximum number of ready-made candidate locators an enriched `SpanAmbiguous` carries. Mirrors
// markup's `locate.ts` CANDIDATE_CAP and the MCP toolkit's CANDIDATE_DISPLAY_CAP so the "N more"
// count stays accurate across all three renderers.
export const CANDIDATE_CAP = 5;

// A half-open head-relative offset span the locator resolved to. A boundary resolves to a
// zero-width point (`from === to`).
export interface ResolvedSpan {
  readonly from: number;
  readonly to: number;
}

const uniqueIndex = (
  haystack: string,
  needle: string,
): Either.Either<number, SpanNotFound | SpanAmbiguous> => {
  const count = occurrences(haystack, needle);
  if (count === 0) return Either.left(new SpanNotFound({ needle }));
  if (count > 1) return Either.left(new SpanAmbiguous({ needle, count }));
  return Either.right(haystack.indexOf(needle));
};

// Resolve a `Locator` against plain text to a unique head-relative `{ from, to }`, or fail
// with `SpanNotFound`/`SpanAmbiguous`. Mirrors markup's `resolveIn` uniqueness semantics
// exactly (span/region/anchored/boundary), minus the CriticMarkup normalization.
export const resolvePlainLocator = (
  text: string,
  locator: Locator,
): Either.Either<ResolvedSpan, SpanNotFound | SpanAmbiguous> => {
  switch (locator._tag) {
    case "span":
      return Either.map(uniqueIndex(text, locator.text), (from) => ({
        from,
        to: from + locator.text.length,
      }));
    case "region":
      return Either.flatMap(uniqueIndex(text, locator.from), (from) =>
        Either.map(uniqueIndex(text, locator.to), (toIndex) => ({
          from,
          to: toIndex + locator.to.length,
        })),
      );
    case "anchored":
      return Either.map(
        uniqueIndex(text, locator.before + locator.text + locator.after),
        (from) => ({
          from: from + locator.before.length,
          to: from + locator.before.length + locator.text.length,
        }),
      );
    case "boundary":
      return Either.map(uniqueIndex(text, locator.anchor), (at) => {
        const point = locator.side === "before" ? at : at + locator.anchor.length;
        return { from: point, to: point };
      });
  }
};

// Count the non-overlapping occurrences of `needle` in `haystack` (empty needle → 0). The shared
// definition of "how many times does this text appear", so the span-anchoring producer
// (`anchorOccurrence`) and the locator resolver (`markup`'s `uniqueIndex`/`resolveIn`) agree on
// exactly what makes a locator unique — a single definition rather than two copies that could drift.
export const occurrences = (haystack: string, needle: string): number => {
  if (needle.length === 0) return 0;
  let count = 0;
  let from = haystack.indexOf(needle);
  while (from >= 0) {
    count++;
    from = haystack.indexOf(needle, from + needle.length);
  }
  return count;
};

/**
 * Build a document-unique `Locator` for the `occurrenceIndex`-th (zero-based) non-overlapping
 * occurrence of `needle` in `docText`.
 *
 * **Return shape:**
 * - `span { text }` when `needle` is globally unique in `docText` (no context needed).
 * - `anchored { before, text, after }` otherwise — symmetric context is widened (doubling each
 *   step from an initial 8-character pad) until `before + needle + after` is the single match in
 *   `docText`, guaranteeing the locator is document-unique and unambiguous to `resolveLocator`.
 *
 * **Termination:** widening is bounded by the document extent. At full-document extent a real
 * occurrence is trivially unique by construction, so the loop always terminates.
 *
 * **Non-overlapping semantics:** occurrence indices are counted by `occurrences`, which advances by
 * `needle.length` after each match — the same counting the `markup` package's `uniqueIndex` /
 * `resolveIn` use, so a locator produced here resolves to exactly the occurrence the caller
 * requested.
 *
 * The caller is responsible for providing a valid `occurrenceIndex` (0 ≤ index < occurrences of
 * needle). Passing an out-of-range index is a programmer error (defect), not a domain failure.
 */
export const anchorOccurrence = (
  docText: string,
  needle: string,
  occurrenceIndex: number,
): Locator => {
  // Locate the byte offset of the requested occurrence.
  let remaining = occurrenceIndex;
  let pos = docText.indexOf(needle);
  while (pos >= 0 && remaining > 0) {
    remaining--;
    pos = docText.indexOf(needle, pos + needle.length);
  }
  // pos is now the start offset of the requested occurrence, or -1 if not found (defect).
  const start = pos;
  const end = start + needle.length;

  // If the needle is globally unique, a bare span suffices.
  if (occurrences(docText, needle) === 1) return span(needle);

  // Widen symmetric context until `before + needle + after` is document-unique.
  let pad = 8;
  for (;;) {
    const head = docText.slice(Math.max(0, start - pad), start);
    const tail = docText.slice(end, Math.min(docText.length, end + pad));
    const atFullExtent = start - pad <= 0 && end + pad >= docText.length;
    if (occurrences(docText, head + needle + tail) === 1 || atFullExtent)
      return anchored(head, needle, tail);
    pad *= 2;
  }
};
